import Big from 'big.js';
import {getCcyDataByCcy, blankCcyData} from '../utils/CurrencyHelper';

export const ROUND = {
	DOWN: 0,
	HALF_UP: 1,
	HALF_EVEN: 2,
	UP: 3,
};

export const getNumber = (value: string, sep: string = ',', decimalPoint: string = '.', decimalDigits: number): number => {
	if (value) {
		const correctedDecimals = correctDecimals(value, decimalDigits, decimalPoint);
		let [left, right] = correctedDecimals.split(decimalPoint);
		left = removeOnlySepNotDecimalPoint(left, sep);
		return parseFloat(left + '.' + (right ? right : ''));
	}
	return 0;
};

// --- before validate with regex, we remove thousand separators ---
export const removeOnlySepNotDecimalPoint = (value: string, sep: string = ','): string => {
	if (value) {
		return value.replace(new RegExp('\\' + sep, 'g'), '');
	}
	return '';
};

export const correctDecimals = (val: string, digits: number, decimalPoint: string = '.'): string => {
	let [left, right] = val.includes(decimalPoint) ? val.split(decimalPoint) : [val, ''];
	if (digits > 0) {
		const decimalPointExists = right !== undefined;
		if (!decimalPointExists) {
			return left + decimalPoint.padEnd(digits + 1, '0');
		}
		if (decimalPointExists) {
			if (val === decimalPoint) {
				return getPlaceholder(digits, decimalPoint);
			}
			return (left ? left : '0') + decimalPoint + (right ? right : '').padEnd(digits, '0');
		}
	}
	return left;
};

export const getPlaceholder = (digits: number, decimalPoint: string = '.'): string => {
	return digits > 0 ? `0${decimalPoint}`.padEnd(digits + 2, '0') : '0'; //0. --> 2
};

export const correctDecimalsBeforeDbSave = (value: number, decimalDigits: number): number => {
	if (value > 0 && decimalDigits > -1) {
		return Big(value).round(decimalDigits).toNumber();
	}
	return 0;
};

export const convertToDisplayValueByCcy = (value: number, ccy: string): string => {
	// 0 is a valid input, so need to consider
	if (ccy) {
		let {thousandsSeparator = ',', decimalDigits, decimalSeparator = '.'} = getCcyDataByCcy(ccy);
		let returnVal = Big(value).toFixed(decimalDigits);
		return addThousandSeparatorToValue(returnVal, thousandsSeparator, decimalSeparator);
	}
	return value.toString();
};

// --- this is from conversion view, point is when user type amount from base ccy, we need to round down(so gets less quote ccy),
export const fromPlainToDisplayValueWithRounding = (
	value: number,
	thousands: string,
	decimalPoint: string,
	decimalDigits: number,
	roundingMode: number,
): string => {
	//set passed rounding mode
	Big.RM = roundingMode;
	const retVal = fromPlainToDisplayValue(value, thousands, decimalPoint, decimalDigits);
	//reset to half even mode
	Big.RM = ROUND.HALF_EVEN;
	return retVal;
};

export const fromPlainToDisplayValue = (value: number, thousands: string, decimalPoint: string, decimalDigits: number): string => {
	let returnVal = Big(value).toFixed(decimalDigits);
	return addThousandSeparatorToValue(returnVal, thousands, decimalPoint);
};

export const addThousandSeparatorToValue = (value: string, thousands: string, decimalPoint: string): string => {
	let [left, right] = value.split('.');
	return addThousandAndConcat(left, right, thousands, decimalPoint);
};

export const addThousandAndConcat = (left: string, right: string, thousands: string, decimalPoint: string): string => {
	left = addThousandToLeftSide(left, thousands);
	if (right && thousands !== decimalPoint) {
		return `${left}${decimalPoint}${right}`;
	} else {
		return left;
	}
};

export const formatRateNumber = (value: number, ccy2: string): string => {
	if (ccy2) {
		const {rateDigits, decimalSeparator} = getCcyDataByCcy(ccy2);
		let withDigits = Big(value).toFixed(rateDigits || blankCcyData.rateDigits);
		let [left, right] = withDigits.split('.');
		return `${left}${decimalSeparator}${right}`;
	}
	return value.toString();
};

export const addThousandToLeftSide = (value: string, thousands: string): string => {
	return value.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
};

// --- on change --- without correcting decimals auto
export const addThousandSeparator = (value: string, thousands: string, decimalPoint: string): string => {
	let [left, right] = value.split(decimalPoint);
	left = addThousandToLeftSide(left, thousands);
	const decimalPointExists = value.indexOf(decimalPoint) > -1;
	if (decimalPointExists) {
		return left + decimalPoint + (right ? right : '');
	}
	return left;
};
